'use client';

import type { School } from '@/types/api';
import { getSchoolLogoUrl, getSchoolInitials } from '@/lib/school-utils';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { schoolsService } from '@/services/schools.service';

interface SchoolLogoProps {
  school: School;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
};

export function SchoolLogo({ school, size = 'md', className = '' }: SchoolLogoProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const primaryColor = school.primaryColor || '#1e40af';
  const initials = getSchoolInitials(school.schoolTitle);
  const sizeClass = sizeClasses[size];
  const padding = size === 'sm' ? 'p-1' : size === 'md' ? 'p-1.5' : 'p-1.5';

  // Get logo URL from school data
  const directLogoUrl = getSchoolLogoUrl(school);
  
  // If we only have logoId, fetch the school to get the logo URL
  const { data: fetchedLogoUrl } = useQuery({
    queryKey: ['school-logo', school.schoolName],
    queryFn: () => schoolsService.getLogoUrlFromSchool(school.schoolName),
    enabled: !directLogoUrl && !!school.logoId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: false, // Don't retry if it fails
  });

  // Use direct URL if available, otherwise use fetched URL
  const logoUrl = directLogoUrl || fetchedLogoUrl || null;

  // Reset image state when logoUrl changes
  useEffect(() => {
    if (logoUrl) {
      setImageLoaded(false);
      setImageError(false);
    }
  }, [logoUrl]);

  // Show placeholder if no logo URL or image failed to load
  const showPlaceholder = !logoUrl || imageError;

  if (showPlaceholder) {
    return (
      <div
        className={`${sizeClass} ${padding} rounded-lg border-2 bg-gradient-to-br from-muted to-muted/80 shadow-sm shrink-0 flex items-center justify-center ${className}`}
        style={{ borderColor: primaryColor }}
      >
        <div
          className="font-bold"
          style={{ color: primaryColor }}
        >
          {initials}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${sizeClass} ${padding} rounded-lg border-2 bg-white shadow-sm shrink-0 flex items-center justify-center relative overflow-hidden ${className}`}
      style={{ borderColor: primaryColor }}
    >
      {/* Placeholder shown while loading */}
      {!imageLoaded && (
        <div
          className="absolute inset-0 bg-gradient-to-br from-muted to-muted/80 flex items-center justify-center"
          style={{ borderColor: primaryColor }}
        >
          <div
            className="font-bold text-xs"
            style={{ color: primaryColor }}
          >
            {initials}
          </div>
        </div>
      )}
      
      {/* Actual image */}
      <img
        src={logoUrl}
        alt={`${school.schoolTitle} mascot`}
        className={`h-full w-full object-contain transition-opacity duration-200 ${
          imageLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        onLoad={() => setImageLoaded(true)}
        onError={() => {
          setImageError(true);
          setImageLoaded(false);
        }}
      />
    </div>
  );
}

